import type { SupabaseClient } from '@supabase/supabase-js';
import { safeReturnTo } from '@garagegymbros/domain';

export function destinationAfterMfa(flow: 'signin' | 'reset', requestedNext: string | null) {
  return flow === 'reset' ? '/auth/reset-password' : safeReturnTo(requestedNext);
}

/** Return a verified TOTP factor only when this session still needs MFA. */
export async function requiredTotpFactor(client: SupabaseClient): Promise<string | null> {
  const assurance = await client.auth.mfa.getAuthenticatorAssuranceLevel();
  if (assurance.error) throw new Error(assurance.error.message);
  if (assurance.data.currentLevel === 'aal2' || assurance.data.nextLevel !== 'aal2') return null;
  const factors = await client.auth.mfa.listFactors();
  if (factors.error) throw new Error(factors.error.message);
  const factor = factors.data.totp.find((item) => item.status === 'verified');
  if (!factor) throw new Error('No verified authenticator is available. Contact account support.');
  return factor.id;
}

/** A recovery session must complete enrolled MFA before sending any new password. */
export async function resetPasswordWithMfa(
  client: SupabaseClient,
  password: string,
): Promise<{ status: 'mfa-required'; factorId: string } | { status: 'updated' }> {
  const factorId = await requiredTotpFactor(client);
  if (factorId) return { status: 'mfa-required', factorId };
  const result = await client.auth.updateUser({ password });
  if (result.error) throw new Error(result.error.message);
  return { status: 'updated' };
}

/** This returns the exact verified factor so enrollment can retain it for later challenges. */
export async function verifyTotpFactor(
  client: SupabaseClient,
  factorId: string,
  code: string,
): Promise<string> {
  if (!factorId) throw new Error('Choose an enrolled authenticator before verifying this session.');
  if (!/^\d{6}$/.test(code)) throw new Error('Enter the 6-digit code from your authenticator.');
  const result = await client.auth.mfa.challengeAndVerify({ factorId, code });
  if (result.error) throw new Error(result.error.message);
  return factorId;
}
